import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Lightbulb, Plus, Download, Upload, Database, Loader2 } from 'lucide-react';
import { ExampleList } from '@/components/few-shot/ExampleList';
import { ExampleForm } from '@/components/few-shot/ExampleForm';
import { ImportExportControls } from '@/components/few-shot/ImportExportControls';
import { OverviewStats } from '@/components/few-shot/OverviewStats';
import { RebuildProgressDialog } from '@/components/filemanagement/RebuildProgressDialog';
import { useAPIClient } from '@/hooks/useAPIClient';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';

type ViewMode = 'list' | 'add' | 'filtered-export' | 'json-import';

export default function FewShotMemory() {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [isRebuildingFewShot, setIsRebuildingFewShot] = useState(false);
  const [showRebuildDialog, setShowRebuildDialog] = useState(false);
  const [activeRebuildTask, setActiveRebuildTask] = useState<{
    taskId: string;
    mode: 'all' | 'documents' | 'few-shot';
  } | null>(null);

  const apiClient = useAPIClient();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();

  const handleRebuildFewShotVdb = async () => {
    setShowRebuildDialog(false);
    setIsRebuildingFewShot(true);
    try {
      const response = await apiClient.documents.rebuildVdb('few-shot');
      console.log('[DEBUG] Rebuild Few-Shot VDB response:', response);

      // Show progress dialog if we got a task_id
      if (response.task_id) {
        console.log('[DEBUG] Setting active few-shot rebuild task:', response.task_id);
        setActiveRebuildTask({
          taskId: response.task_id,
          mode: 'few-shot',
        });
      } else {
        // Fallback for immediate completion (shouldn't happen with new backend)
        toast({
          title: "VDB neu aufgebaut",
          description: "Beispiele wurden neu indiziert",
        });
        await queryClient.invalidateQueries({ queryKey: ['few-shot'] });
      }
    } catch (error) {
      console.error('Rebuild Few-Shot VDB failed:', error);
      toast({
        title: "VDB Neuaufbau fehlgeschlagen",
        description: "Bitte erneut versuchen",
        variant: "destructive",
      });
    } finally {
      setIsRebuildingFewShot(false);
    }
  };

  return (
    <div className="w-full px-4 lg:px-6 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Lightbulb className="h-8 w-8 text-yellow-600" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Beispiel-Datenbank</h1>
            <p className="text-muted-foreground">
              Beispiele zur Verbesserung der Chatbot-Antworten
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {/* Rebuild VDB button - Desktop version */}
          <Button
            variant="outline"
            onClick={() => setShowRebuildDialog(true)}
            disabled={!isAdmin || isRebuildingFewShot}
            title={!isAdmin ? "Nur für Administratoren" : undefined}
            className="hidden sm:flex"
          >
            {isRebuildingFewShot ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Neuaufbau...
              </>
            ) : (
              <>
                <Database className="mr-2 h-4 w-4" />
                VDB neu aufbauen
              </>
            )}
          </Button>

          {/* Rebuild VDB button - Mobile version (icon only) */}
          <Button
            variant="outline"
            onClick={() => setShowRebuildDialog(true)}
            disabled={!isAdmin || isRebuildingFewShot}
            className="sm:hidden"
            title={!isAdmin ? "Nur für Administratoren" : "VDB neu aufbauen"}
          >
            {isRebuildingFewShot ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Database className="h-4 w-4" />
            )}
          </Button>

          {isAdmin && (
            <>
              <Button
                variant={viewMode === 'filtered-export' ? 'default' : 'outline'}
                onClick={() => setViewMode('filtered-export')}
              >
                <Download className="mr-2 h-4 w-4" />
                Gefiltert exportieren
              </Button>
              <Button
                onClick={() => setViewMode('add')}
                className="bg-green-600 hover:bg-green-700 text-white"
                variant={viewMode === 'add' ? 'default' : 'secondary'}
              >
                <Plus className="mr-2 h-4 w-4" />
                Neues Beispiel
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Overview Statistics */}
      <OverviewStats />

      {/* Main Content */}
      {viewMode === 'add' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Neues Beispiel hinzufügen</CardTitle>
                <CardDescription>
                  Erstellen Sie ein neues Frage-Antwort-Paar zur Verbesserung der KI-Antworten
                </CardDescription>
              </div>
              <Button
                variant="outline"
                onClick={() => setViewMode('json-import')}
              >
                <Upload className="mr-2 h-4 w-4" />
                Importiere JSON
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <ExampleForm
              onSuccess={() => setViewMode('list')}
              onCancel={() => setViewMode('list')}
            />
          </CardContent>
        </Card>
      )}

      {viewMode === 'filtered-export' && (
        <div>
          <div className="mb-4 flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setViewMode('list')}
            >
              ← Zurück zur Beispielsammlung
            </Button>
          </div>
          <ImportExportControls mode="export-only" />
        </div>
      )}

      {viewMode === 'json-import' && (
        <div>
          <div className="mb-4 flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setViewMode('list')}
            >
              ← Zurück zur Beispielsammlung
            </Button>
          </div>
          <ImportExportControls mode="import-only" />
        </div>
      )}

      {viewMode === 'list' && (
        <Card>
          <CardHeader>
            <CardTitle>Beispielsammlung</CardTitle>
            <CardDescription>
              Durchsuchen, bearbeiten und verwalten Sie Ihre Frage-Antwort-Beispiele
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ExampleList />
          </CardContent>
        </Card>
      )}

      {/* Rebuild VDB Confirmation Dialog */}
      <AlertDialog open={showRebuildDialog} onOpenChange={setShowRebuildDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Few-Shot-VDB neu aufbauen?</AlertDialogTitle>
            <AlertDialogDescription>
              Dies wird die Vektordatenbank für alle Beispiele neu aufbauen. Der Vorgang kann einige Minuten dauern und während dieser Zeit ist die App für andere Nutzer nicht verwendbar. Möchten Sie fortfahren?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleRebuildFewShotVdb}>
              Fortfahren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rebuild Progress Dialog */}
      {activeRebuildTask && (
        <RebuildProgressDialog
          taskId={activeRebuildTask.taskId}
          mode={activeRebuildTask.mode}
          onComplete={() => {
            // Refresh few-shot data when rebuild completes
            queryClient.invalidateQueries({ queryKey: ['few-shot'] });
          }}
          onClose={() => {
            setActiveRebuildTask(null);
          }}
        />
      )}
    </div>
  );
}